import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { buildSystemPromptFromTemplate } from '@workspace/types';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import type { ContactAgentAvailableBot } from '@workspace/types';
import { leadsApi } from '../api/leadsApi';
import { contactAgentError } from '../../contact-agent/api/contactAgentScope';
import { ContactAgentPage, ContactAgentPageHeader } from '../../contact-agent/components/ContactAgentPageHeader';
import { ContactAgentTenantPicker } from '../../contact-agent/components/ContactAgentTenantPicker';
import { useContactAgentTenant } from '../../contact-agent/hooks/useContactAgentTenant';
import { LEADS_TOOL_OPTIONS, leadsToolsToPersist } from '../api/leadsTools';

type BotOption = ContactAgentAvailableBot;

export function LeadsAgentPage() {
  const navigate = useNavigate();
  const { agentId } = useParams<{ agentId: string }>();
  const isNew = !agentId || agentId === 'new';
  const { tenantId, tenants, isSuperAdmin, ready, selectTenant } = useContactAgentTenant();
  const [bots, setBots] = useState<BotOption[]>([]);
  const [botId, setBotId] = useState('');
  const [name, setName] = useState('Embudo de leads');
  const [objective, setObjective] = useState(
    'Calificar leads entrantes, relevar magnitud de la operación y derivar al comercial con ficha estructurada.',
  );
  const [systemPrompt, setSystemPrompt] = useState('');
  const [tools, setTools] = useState<string[]>(LEADS_TOOL_OPTIONS.map(item => item.name));
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!ready) {
      return;
    }
    if (!isNew && agentId) {
      void leadsApi
        .getAgent(agentId)
        .then(agent => {
          if (agent.domain !== 'LEADS') {
            throw new Error('El agente no pertenece al embudo de leads');
          }
          setBotId(agent.botId);
          setName(agent.name);
          setObjective(agent.objective ?? '');
          setTools(agent.toolsEnabled.length > 0 ? agent.toolsEnabled : LEADS_TOOL_OPTIONS.map(item => item.name));
          setSystemPrompt(agent.bot?.systemPrompt ?? '');
        })
        .catch(err => setError(contactAgentError(err)));
      return;
    }

    setBotId('');
    setSystemPrompt(buildSystemPromptFromTemplate('sales'));
    setError('');
    void leadsApi
      .listAvailableBots()
      .then(options => {
        setBots(options);
        const firstEligible = options.find(bot => bot.identificationEligible);
        if (firstEligible) {
          setBotId(firstEligible.botId);
        } else if (options[0]) {
          setBotId(options[0].botId);
        }
      })
      .catch(err => setError(contactAgentError(err)));
  }, [ready, tenantId, isNew, agentId]);

  const selectedBot = bots.find(bot => bot.botId === botId);

  const toggleTool = (toolName: string) => {
    setTools(current =>
      current.includes(toolName) ? current.filter(item => item !== toolName) : [...current, toolName],
    );
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!botId || !name.trim()) {
      setError('Elegí un bot y un nombre.');
      return;
    }
    if (isNew && selectedBot && !selectedBot.identificationEligible) {
      setError(
        selectedBot.identificationIssues[0] ??
          'El bot debe tener al menos un método de identificación unívoca.',
      );
      return;
    }
    if (!systemPrompt.trim()) {
      setError('El prompt del bot no puede quedar vacío.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: name.trim().slice(0, 255),
        objective: objective.trim().slice(0, 4000),
        toolsEnabled: leadsToolsToPersist(tools),
        systemPrompt: systemPrompt.trim().slice(0, 32000),
      };
      if (isNew) {
        await leadsApi.createAgent({
          botId,
          ...payload,
        });
      } else if (agentId) {
        await leadsApi.updateAgent(agentId, payload);
      }
      navigate('/admin/leads');
    } catch (err) {
      setError(contactAgentError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ContactAgentPage>
      <ContactAgentPageHeader
        title={isNew ? 'Nuevo agente de leads' : 'Editar agente de leads'}
        description='Sin cartera ni medios de pago. El bot califica y deriva al comercial.'
        backTo='/admin/leads'
        toolbar={
          isSuperAdmin ? (
            <ContactAgentTenantPicker tenants={tenants} tenantId={tenantId} onChange={selectTenant} />
          ) : null
        }
      />

      {error ? <p className='text-sm text-destructive'>{error}</p> : null}

      <form onSubmit={handleSubmit} className='space-y-6'>
        <Card className='rounded-2xl'>
          <CardHeader>
            <CardTitle>Configuración</CardTitle>
          </CardHeader>
          <CardContent className='space-y-4'>
            {isNew ? (
              <div className='space-y-2'>
                <Label htmlFor='botId'>Bot</Label>
                <select
                  id='botId'
                  className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm'
                  value={botId}
                  onChange={event => setBotId(event.target.value)}
                >
                  <option value=''>Seleccionar bot</option>
                  {bots.map(bot => (
                    <option key={bot.botId} value={bot.botId}>
                      {bot.name}
                      {!bot.identificationEligible ? ' (requiere identificación)' : ''}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}
            <div className='space-y-2'>
              <Label htmlFor='name'>Nombre del agente</Label>
              <Input id='name' value={name} onChange={event => setName(event.target.value)} />
            </div>
            <div className='space-y-2'>
              <Label htmlFor='objective'>Objetivo</Label>
              <Textarea id='objective' value={objective} onChange={event => setObjective(event.target.value)} rows={3} />
            </div>
            <div className='space-y-2'>
              <Label htmlFor='systemPrompt'>Prompt del bot</Label>
              <Textarea
                id='systemPrompt'
                value={systemPrompt}
                onChange={event => setSystemPrompt(event.target.value)}
                rows={10}
              />
            </div>
            <div className='space-y-2'>
              <Label>Herramientas</Label>
              <div className='flex flex-wrap gap-2'>
                {LEADS_TOOL_OPTIONS.map(tool => (
                  <Button
                    key={tool.name}
                    type='button'
                    size='sm'
                    variant={tools.includes(tool.name) ? 'default' : 'outline'}
                    onClick={() => toggleTool(tool.name)}
                  >
                    {tool.label}
                  </Button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <div className='flex gap-2'>
          <Button type='submit' disabled={saving}>
            {saving ? 'Guardando…' : 'Guardar'}
          </Button>
          <Button type='button' variant='outline' asChild>
            <Link to='/admin/leads'>Cancelar</Link>
          </Button>
        </div>
      </form>
    </ContactAgentPage>
  );
}
